// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable formatjs/no-literal-string-in-jsx */
import { BookingDetailsStep } from "#/features/telemedicine/booking/BookingDetailsStep"
import { bookingMachine, BookingMachineEvent } from "#/features/telemedicine/booking/bookingMachine"
import { InsuranceDetailsStep } from "#/features/telemedicine/booking/InsuranceDetailsStep"
import { PaymentStep } from "#/features/telemedicine/booking/PaymentStep"
import { Typography } from "@carrotfertility/carotene-core"
import { FormProvider, useForm } from "@carrotfertility/carotene-core-x"
import { useMachine } from "@xstate/react"
import React from "react"

type BookingFormValues = {
  appointment: {
    type: string
    locationOfCare: string
    date?: string
    time?: string
  }
  payment: {
    method: string
  }
  card: {
    name: string
    number: string
    expiry: string
    cvc: string
    zip: string
    saveCard?: boolean
  }
}

// Every state the form has fields/a submit action for — mirrors bookingMachine.ts.
// Kept as one flat type so rendering and submit-handling both key off the same
// resolved value instead of each separately re-deriving "which step" via matches().
type Step =
  | "enteringDetails"
  | "loadingAvailability"
  | "choosingDate"
  | "loadingTimes"
  | "choosingTime"
  | "collectingInsurance"
  | "collectingPayment"
  | "complete"

const DETAILS_FIELDS = ["appointment.type", "appointment.locationOfCare", "payment.method"] as const
const DATE_FIELDS = ["appointment.date"] as const
const TIME_FIELDS = ["appointment.time"] as const
const CARD_FIELDS = ["card.name", "card.number", "card.expiry", "card.cvc", "card.zip"] as const

export function BookingForm() {
  const methods = useForm<BookingFormValues>()
  const { trigger, getValues } = methods
  const [state, send] = useMachine(bookingMachine)

  let step: Step = "enteringDetails"
  if (state.matches("loadingAvailability")) step = "loadingAvailability"
  else if (state.matches({ choosingDateTime: "choosingDate" })) step = "choosingDate"
  else if (state.matches({ choosingDateTime: "loadingTimes" })) step = "loadingTimes"
  else if (state.matches({ choosingDateTime: "choosingTime" })) step = "choosingTime"
  else if (state.matches("collectingInsurance")) step = "collectingInsurance"
  else if (state.matches("collectingPayment")) step = "collectingPayment"
  else if (state.matches("complete")) step = "complete"

  // Each step validates only its own fields, then sends its own event — a table
  // lookup by `step` instead of branching, so a new step means a new row here
  // rather than another else-if.
  const submitByStep: Partial<
    Record<Step, { fields: Parameters<typeof trigger>[0]; toEvent: () => BookingMachineEvent }>
  > = {
    enteringDetails: { fields: DETAILS_FIELDS, toEvent: () => ({ type: "SEE_AVAILABILITY" }) },
    choosingDate: {
      fields: DATE_FIELDS,
      toEvent: () => ({ type: "DATE_CHOSEN", date: getValues("appointment.date") ?? "" })
    },
    choosingTime: {
      fields: TIME_FIELDS,
      toEvent: () => ({ type: "TIME_CHOSEN", paymentMethod: getValues("payment.method") })
    },
    collectingInsurance: { fields: [], toEvent: () => ({ type: "CONTINUE" }) },
    collectingPayment: { fields: CARD_FIELDS, toEvent: () => ({ type: "CONTINUE" }) }
  }

  // One <form> for the whole flow, so RHF (not the machine) is the source of
  // truth for field values — going back to an earlier step re-shows whatever was
  // already typed, with nothing to snapshot/restore by hand.
  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const submit = submitByStep[step]
    if (submit && (await trigger(submit.fields))) send(submit.toEvent())
  }

  const handleBack = () => send({ type: "BACK" })

  let content: React.ReactNode
  if (step === "collectingInsurance") {
    content = <InsuranceDetailsStep onBack={handleBack} />
  } else if (step === "collectingPayment") {
    content = <PaymentStep onBack={handleBack} />
  } else if (step === "complete") {
    content = <Typography>Booking confirmed — we&apos;ll see you then!</Typography>
  } else {
    content = (
      <BookingDetailsStep
        availableDates={state.context.availableDates}
        availabilityError={state.context.availabilityError}
        availableTimes={state.context.availableTimes}
        timesError={state.context.timesError}
        step={step}
      />
    )
  }

  return (
    <FormProvider {...methods}>
      <form noValidate onSubmit={handleSubmit}>
        {content}
      </form>
    </FormProvider>
  )
}
